import { lazy } from 'react';
import type { RouteObject } from 'react-router';

import { OverviewPage } from '@/app/pages/overview';
import { AppShell } from '@/app/shell/app-shell';
import { PersonRegister } from '@/features/persons/register';
import { GateScreen } from '@/features/scan/gate';

/**
 * The browser routes, as plain objects.
 *
 * They are data rather than a `createBrowserRouter` call so the application can be mounted on a
 * memory router in a test and on a history router in a browser, from one definition. A test that
 * built its own route table would be testing a second answer to "what screens does this product
 * have", which is the same mistake the server's route registry exists to prevent.
 *
 * **Every section renders its own screens now, and the placeholder is gone with them.** It existed
 * so that the shell's navigation, its keyboard traversal and its route-scoped idle window had
 * somewhere to go before the screens landed; a component that nothing renders is dead weight the
 * dead-code gate is right to refuse.
 *
 * **Administration is the one section with children, and the one that is loaded on demand.** Its
 * own screens are a second level of navigation rather than ten more entries in the sidebar,
 * because an operator at a gate should not be scrolling past the backup screen to reach the
 * register — and because the whole section shares one idle window, which `app/navigation.ts`
 * decides from the path prefix.
 *
 * **All ten resolve ONE module, `features/admin/screens.ts`, through `import()`.** That module is
 * the bundle's only dynamic boundary: everything reachable exclusively through it lands in a chunk
 * of its own, which `packages/web/vite.config.ts` holds to a ceiling of its own rather than to the
 * one that bounds the code every operator downloads to reach any screen. One boundary rather than
 * ten is what makes opening the section cost one request and walking between its screens cost
 * none — and the gate and the register stay in the eager chunk exactly as they were, because
 * `vite.config.ts`'s "one chunk, deliberately" reasoning is about an operator walking to a
 * different screen with somebody standing in front of them.
 *
 * **The catch-all renders the overview rather than a not-found screen.** This is an authenticated
 * tool with four sections and no deep-linkable public surface; a mistyped path is somebody's stale
 * bookmark, and putting them on the first screen is more useful than telling them their URL was
 * wrong. A section that later gains deep links will want its own not-found inside itself, which is
 * where the useful message can name what was not found.
 */

/** The one dynamic import in this application. Every lazy screen below resolves it. */
const admin = () => import('@/features/admin/screens');

const AdminScreen = lazy(async () => ({ default: (await admin()).AdminScreen }));
const AnalyticsScreen = lazy(async () => ({ default: (await admin()).AnalyticsScreen }));
const ApiKeysScreen = lazy(async () => ({ default: (await admin()).ApiKeysScreen }));
const AuditScreen = lazy(async () => ({ default: (await admin()).AuditScreen }));
const BackupsScreen = lazy(async () => ({ default: (await admin()).BackupsScreen }));
const ExportsScreen = lazy(async () => ({ default: (await admin()).ExportsScreen }));
const FieldsScreen = lazy(async () => ({ default: (await admin()).FieldsScreen }));
const IncidentsScreen = lazy(async () => ({ default: (await admin()).IncidentsScreen }));
const PassesScreen = lazy(async () => ({ default: (await admin()).PassesScreen }));
const PersonImportScreen = lazy(async () => ({
  default: (await admin()).PersonImportScreen,
}));
const SettingsScreen = lazy(async () => ({ default: (await admin()).SettingsScreen }));
const UsersScreen = lazy(async () => ({ default: (await admin()).UsersScreen }));

export const APP_ROUTES: readonly RouteObject[] = [
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: <OverviewPage /> },
      { path: 'scan', element: <GateScreen /> },
      { path: 'persons', element: <PersonRegister /> },
      {
        path: 'admin',
        element: <AdminScreen />,
        children: [
          { index: true, element: <AnalyticsScreen /> },
          { path: 'users', element: <UsersScreen /> },
          { path: 'fields', element: <FieldsScreen /> },
          { path: 'import', element: <PersonImportScreen /> },
          { path: 'settings', element: <SettingsScreen /> },
          { path: 'api-keys', element: <ApiKeysScreen /> },
          { path: 'audit', element: <AuditScreen /> },
          { path: 'incidents', element: <IncidentsScreen /> },
          { path: 'exports', element: <ExportsScreen /> },
          { path: 'backups', element: <BackupsScreen /> },
          { path: 'passes', element: <PassesScreen /> },
        ],
      },
      { path: '*', element: <OverviewPage /> },
    ],
  },
];
